// Which school year is being viewed, and whether that is the school's active
// one. Two consumers ask this: the navigation registry (the Canvas tab is
// current-year only) and the School navbar (it paints an inactive year orange),
// so the comparison lives here rather than being restated in each.
//
// Deliberately derived on the client. `school.current_year` (the active year's
// id) ships in the app-props blob, which Rails serializes once per document and
// is read once at mount - it is documented as data that does not change after
// first load. The *selected* year does change without a document load: it is a
// URL path segment in the SPA and a localStorage value on bridged legacy pages.
// A server-computed boolean would therefore be right at mount and stale the
// moment the user picks another year from the select.

// The storage key the legacy frontend has always used for the staff's selected
// year: v1 API calls send it as the X-School-Year-ID header, the bridged chrome
// reads it at mount, and the SPA layout mirrors the URL year segment into it.
export const SCHOOL_YEAR_STORAGE_KEY = "current_school_year";

const readStoredSchoolYearId = () => {
  try {
    return window.localStorage.getItem(SCHOOL_YEAR_STORAGE_KEY);
  } catch (e) {
    // storage can throw in private mode / blocked storage
    return null;
  }
};

// The year that year-scoped links should resolve against when building URLs.
// Precedence: the year the page is already scoped to (URL segment / bridge
// prop), then the staff's last selection from the legacy store, then the
// school's active year, then the first year the school has. The stored value
// only wins when it belongs to this school's year list - a leftover from
// another school (the switcher clears it, but a direct cross-school link
// skips the switcher) must not leak into links.
export const resolveViewedYearId = (appProps, schoolYearId = null) => {
  if (schoolYearId) return schoolYearId;
  const years = appProps?.school_years ?? [];
  const stored = readStoredSchoolYearId();
  if (stored && years.some((y) => String(y.id) === stored)) return stored;
  return appProps?.school_year?.id ?? years[0]?.id ?? null;
};

// True while `yearId` is the school's current (active) year. Both sides are
// stringified: the id arrives as a number in appProps but as a string from the
// URL path segment / localStorage.
export const isViewingCurrentYear = (school, yearId) =>
  school?.current_year != null &&
  String(school.current_year) === String(yearId);

// True when the chrome should show its inactive-year warning. Two guards beyond
// the year comparison, both matching the legacy navbar this restores:
//   - No year in scope (`/messages`, `/notifications` and other non-year-scoped
//     pages) - there is nothing to be wrong about, stay neutral.
//   - A school with no school years at all - the comparison is meaningless, and
//     warning on a school that simply has not been set up yet is noise.
export const isInactiveSchoolYear = (appProps, schoolYearId) =>
  Boolean(schoolYearId) &&
  (appProps?.school_years?.length ?? 0) > 0 &&
  !isViewingCurrentYear(appProps?.school, schoolYearId);
